import { Navigate, Route, Routes, useParams } from "react-router-dom";
import "../src/styles/globals.scss";
import { LoginPage } from "./root/auth/modules/pages/LoginPage";
import { ForgotPasswordPage } from "./root/auth/modules/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./root/auth/modules/pages/ResetPasswordPage";
import { CarMutatePage } from "./root/car/modules/pages/CarMutatePage";
import { RelationMutatePage } from "./root/relation/modules/pages/RelationMutatePage";
import { useAppSelector } from "./hooks/useTypedSelector";
import { PublicRoute } from "./shared/route/commons/publicRoute";
import { ProtectedRoute } from "./shared/route/commons/protectedRoute";
import { RelationsPage } from "./root/relation/modules/pages/RelationsPage";
import { RelationCustomersPage } from "./root/relation/modules/pages/RelationCustomersPage";
import { RelationCustomerMutateModal } from "./root/relation/modules/modals/RelationCustomerMutateModal";
import { RelationUsersPage } from "./root/relation/modules/pages/RelationUsersPage";
import { RelationUserMutateModal } from "./root/relation/modules/modals/RelationUserMutateModal";
import { RequestMutatePage } from "./root/request/modules/pages/RequestMutatePage";
import { TireSetsPage } from "./root/tireset/modules/pages/TireSetsPage";
import { EntitySelectFieldCreateModal } from "./root/general/fields/modules/commons/entitySelectField/entitySelectField";
import { WarehousePage } from "./root/storage/warehouse/modules/pages/WarehousePage";
import { PathPage } from "./root/storage/path/modules/pages/PathPage";
import { PositionPage } from "./root/storage/position/modules/pages/PositionPage";
import { WarehouseMutatePage } from "./root/storage/warehouse/modules/pages/WarehouseMutatePage";
import { PositionMutatePage } from "./root/storage/position/modules/pages/PositionMutatePage";
import { PathMutatePage } from "./root/storage/path/modules/pages/PathMutatePage";
import { RedirectTiresetToCar } from "./root/car/modules/commons/redirectTiresetToCar/redirectTiresetToCar";
import { RequestsPage } from "./root/request/modules/pages/RequestsPage";
import { UserMutatePage } from "./root/user/modules/pages/UserMutatePage";
import { UserPage } from "./root/user/modules/pages/UserPage";
import { LogsPage } from "./root/logs/modules/pages/LogsPage";
import { RequestCreatePage } from "./root/request/modules/pages/RequestCreatePage";
import { CarPage } from "./root/car/modules/pages/CarPage";
import { DailyPlanningPage } from "./root/planning/dailyPlanning/modules/pages/DailyPlanningPage";
import { TruckPage } from "./root/planning/truck/modules/pages/TruckPage";
import { TruckMutatePage } from "./root/planning/truck/modules/pages/TruckMutatePage";
import { RoutesPage } from "./root/planning/route/modules/pages/RoutesPage";
import { PackingSlipPage } from "./root/planning/packingSlip/modules/pages/PackingSlipPage";
import { PlanningSettingsMutatePage } from "./root/planning/settings/modules/pages/SettingsMutatePage";
import { Page404 } from "./root/general/modules/pages/Page404";
import { Spinner } from "react-bootstrap";
import { InvoicesPage } from "./root/invoice/modules/pages/InvoicesPage";
import { RouteSettingsModal } from "./root/planning/dailyPlanning/modules/features/RouteSettingsModal";
import { PackingSlipDetailPage } from "./root/planning/packingSlip/modules/pages/PackingSlipDetailPage";
import { DefaultRoutePage } from "./root/planning/defaultRoute/modules/pages/DefaultRoutePage";
import { DefaultRouteMutatePage } from "./root/planning/defaultRoute/modules/pages/DefaultRouteMutatePage";
import { EventPage } from "./root/event/modules/pages/EventPage";
import { ShelvingUnitPage } from "./root/storage/shelvingUnit/modules/pages/ShelvingUnitPage";
import { ShelvingUnitMutatePage } from "./root/storage/shelvingUnit/modules/pages/ShelvingUnitMutatePage";
import { PackingListPage } from "./root/planning/packingList/modules/pages/PackingListPage";
import { RelationAddExistingUserModal } from "./root/relation/modules/modals/RelationAddExistingUserModal";
import { EntityLogPage } from "./root/logs/modules/pages/EntityLogPage";
import { RelationImportPage } from "./root/relation/modules/pages/RelationImportPage";
import { DashboardPage } from "./root/dashboard/modules/pages/DashboardPage";
import { RequestQuickAddPage } from "./root/request/modules/pages/RequestQuickAddPage";

function RedirectTireSetCar() {
  const { id } = useParams();
  return <RedirectTiresetToCar id={id as string} />;
}

function RedirectPosition() {
  const { id } = useParams();
  return <Navigate to={`/positions/edit/${id as string}`} />;
}

function App() {
  const { user, loading } = useAppSelector((state) => state.auth);

  return (
    <>
      {loading && (
        <div className={"spinner-wrapper"}>
          <Spinner animation="grow" role="status" variant={"danger"}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      <Routes>
        <Route
          index
          path="/"
          element={
            <PublicRoute user={user}>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/auth/login"
          element={
            <PublicRoute user={user}>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/auth/forgot-password"
          element={
            <PublicRoute user={user}>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <PublicRoute user={user}>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["dealer-employee", "super-admin"]}
            >
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tire-sets"
          element={
            <ProtectedRoute user={user}>
              <TireSetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cars"
          element={
            <ProtectedRoute user={user}>
              <CarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/car/add"
          element={
            <ProtectedRoute user={user}>
              <CarMutatePage />
            </ProtectedRoute>
          }
        >
          <Route
            path="/car/add/add"
            element={<EntitySelectFieldCreateModal />}
          />
        </Route>
        <Route
          path="/car/:id/edit"
          element={
            <ProtectedRoute user={user}>
              <CarMutatePage />
            </ProtectedRoute>
          }
        >
          <Route
            path="/car/:id/edit/add"
            element={<EntitySelectFieldCreateModal />}
          />
        </Route>
        {/* Redirects the QR code to the car edit page, the ID is set to the car ID in the QR code generation */}
        <Route
          path="/tireset/:id"
          element={
            <ProtectedRoute user={user}>
              <RedirectTireSetCar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relations"
          element={
            <ProtectedRoute user={user}>
              <RelationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relations/add"
          element={
            <ProtectedRoute user={user}>
              <RelationMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relations/edit/:id"
          element={
            <ProtectedRoute user={user}>
              <RelationMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relations/edit/:id/customers"
          element={
            <ProtectedRoute user={user}>
              <RelationCustomersPage />
            </ProtectedRoute>
          }
        >
          <Route
            path="/relations/edit/:id/customers/add"
            element={<RelationCustomerMutateModal />}
          />

          <Route
            path="/relations/edit/:id/customers/:customerId/edit"
            element={<RelationCustomerMutateModal />}
          />
        </Route>
        <Route
          path="/relations/edit/:id/import-licence-plates"
          element={
            <ProtectedRoute user={user}>
              <RelationImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relations/edit/:id/employees"
          element={
            <ProtectedRoute user={user}>
              <RelationUsersPage />
            </ProtectedRoute>
          }
        >
          <Route
            path="/relations/edit/:id/employees/add"
            element={<RelationUserMutateModal />}
          />

          <Route
            path="/relations/edit/:id/employees/add-existing"
            element={<RelationAddExistingUserModal />}
          />

          <Route
            path="/relations/edit/:id/employees/:employeeId/edit"
            element={<RelationUserMutateModal />}
          />
        </Route>
        <Route
          path="/requests/:id"
          element={
            <ProtectedRoute user={user}>
              <RequestMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tire-sets/:tiresetId/create-request"
          element={
            <ProtectedRoute user={user}>
              <RequestCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/requests/create"
          element={
            <ProtectedRoute user={user}>
              <RequestQuickAddPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/requests"
          element={
            <ProtectedRoute user={user}>
              <RequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <InvoicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warehouses"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <WarehousePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warehouses/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <WarehouseMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warehouses/edit/:id"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <WarehouseMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/paths"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PathPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/paths/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PathMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/paths/edit/:id"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PathMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shelvingUnits"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <ShelvingUnitPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shelvingUnits/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <ShelvingUnitMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shelvingUnits/edit/:id"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <ShelvingUnitMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/positions"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PositionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/positions/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PositionMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/positions/edit/:id"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PositionMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/position/:id"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <RedirectPosition />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <UserPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="user/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <UserMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="user/edit/:id"
          element={
            <ProtectedRoute user={user}>
              <UserMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planning/daily-planning"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <DailyPlanningPage />
            </ProtectedRoute>
          }
        >
          <Route
            path="/planning/daily-planning/:routeId"
            element={<RouteSettingsModal />}
          />
        </Route>
        <Route
          path="/planning/default-routes"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <DefaultRoutePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planning/default-route/:id/edit"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <DefaultRouteMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planning/default-route/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <DefaultRouteMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trucks"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <TruckPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/truck/:id/edit"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <TruckMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/truck/add"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <TruckMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/routes"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <RoutesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="packing-slips"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={[
                "super-admin",
                "dbh-manager",
                "dbh-employee",
                "dbh-order-picker",
                "dbh-driver",
                "dealer-employee",
              ]}
            >
              <PackingSlipPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="packing-slips/edit/:id"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={[
                "super-admin",
                "dbh-manager",
                "dbh-employee",
                "dealer-employee",
              ]}
            >
              <PackingSlipDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="packing-lists"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={[
                "super-admin",
                "dbh-manager",
                "dbh-employee",
                "dbh-order-picker",
                "dbh-driver",
                "dealer-employee",
              ]}
            >
              <PackingListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="planning/settings"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <PlanningSettingsMutatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <LogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <EventPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs/:filters"
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["super-admin", "dbh-manager"]}
            >
              <EntityLogPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={
            <ProtectedRoute user={user}>
              <Page404 />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;
